import { useLocation, Link as RouterLink } from "react-router-dom";
import { Check, Globe } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { localizePath, extractLocaleFromPath } from "@/lib/i18n/localize-path";
import { locales, LOCALE_LABELS, type Locale } from "@/lib/i18n/config";

const LOCALE_FLAGS: Record<Locale, string> = {
  en: "🇺🇸",
  "pt-BR": "🇧🇷",
  es: "🇪🇸",
  id: "🇮🇩",
  th: "🇹🇭",
  de: "🇩🇪",
};

export function LanguageModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t, locale: currentLocale } = useLocale();
  const location = useLocation();
  const { path: pathWithoutLocale } = extractLocaleFromPath(location.pathname);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm gap-0 overflow-hidden rounded-[2rem] border-4 border-white bg-white p-0 shadow-2xl">
        <DialogHeader className="flex-row items-center gap-3 px-6 pb-2 pt-6 text-left">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
            <Globe className="size-5 text-primary" />
          </div>
          <div>
            <DialogTitle className="text-xl font-black tracking-tight">{t("language.title")}</DialogTitle>
            <p className="text-sm font-medium text-muted-foreground">{t("language.subtitle")}</p>
          </div>
        </DialogHeader>

        <div className="space-y-1.5 px-4 py-4">
          {locales.map((locale: Locale) => {
            const isCurrent = locale === currentLocale;
            return (
              <RouterLink
                key={locale}
                to={localizePath(pathWithoutLocale, locale)}
                onClick={() => onOpenChange(false)}
                className={`flex items-center gap-3 rounded-2xl border-2 px-4 py-3 transition-all ${
                  isCurrent
                    ? "border-primary/30 bg-primary/10"
                    : "border-transparent hover:scale-[1.01] hover:border-border hover:bg-secondary/60"
                }`}
              >
                <span className="text-xl">{LOCALE_FLAGS[locale]}</span>
                <span className="flex-1 text-sm font-bold text-foreground">{LOCALE_LABELS[locale]}</span>
                {isCurrent ? (
                  <span className="flex size-6 items-center justify-center rounded-full bg-primary text-white">
                    <Check className="size-3.5" />
                  </span>
                ) : null}
              </RouterLink>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
